import { useEffect, useState, useCallback } from "react";
import { axiosInstance } from "../../../utility/apiInstance";
import toast from "react-hot-toast";

export interface Milestone {
  _id?: string;
  mileStone: string;
  container_number: string;
  portName?: string;
  location?: string;
  expectedDate?: string | null;
  date?: string;
  vesselName?: string;
  portCode?: string;
  portType?: string;
  transportType?: string;
  country?: string;
  status?: number;
}

export type ContainerWiseMilestones = {
  [containerNumber: string]: Milestone[];
};

export const useOfflineOceanMilestones = (awbNumber: string) => {
  const [uniqueMilestones, setUniqueMilestones] = useState<Milestone[]>([]);
  const [allMilestones, setAllMilestones] = useState<Milestone[]>([]);
  const [containerWiseData, setContainerWiseData] =
    useState<ContainerWiseMilestones>({});
  const [containerData, setContainerData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<boolean>(false);

  /* ================= FETCH ALL ================= */

  const buildContainerWiseStructure = useCallback((data: Milestone[]) => {
    const grouped = data.reduce<ContainerWiseMilestones>((acc, item) => {
      if (!acc[item.container_number]) {
        acc[item.container_number] = [];
      }
      acc[item.container_number].push(item);
      return acc;
    }, {});
    setContainerWiseData(grouped);
  }, []);

  const fetchMilestones = useCallback(async () => {
    if (!awbNumber) return;

    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `/api/v1/offline-ocean-milestone/${awbNumber}`
      );

      const unique = data?.data?.uniqueMileStone || [];
      const all = data?.data?.allfinalMileStone || [];

      setUniqueMilestones(unique);
      setAllMilestones(all);
      buildContainerWiseStructure(all);
    } catch (error) {
      toast.error("Failed to load milestones");
    } finally {
      setLoading(false);
    }
  }, [awbNumber, buildContainerWiseStructure]);

  const fetchContainers = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `api/v1/ocean/offilne-container/${awbNumber}`
      );
      setContainerData(response?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  }, [awbNumber]);

  useEffect(() => {
    fetchMilestones();
    fetchContainers();
  }, [fetchMilestones, fetchContainers]);

  


  const selectMilestone = (milestone: Milestone) => {
    if (!milestone) return;

    const grouped = containerData.reduce<ContainerWiseMilestones>(
      (acc, container) => {
        const containerNumber = container.container_number;

        const existing = allMilestones.find(
          (m) =>
            String(m.container_number) === String(containerNumber) &&
            m.mileStone?.trim() === milestone.mileStone?.trim()
        );

        acc[containerNumber] = [
          existing
            ? { ...existing } // ✅ preserve expectedDate
            : {
              ...milestone,
              container_number: containerNumber,
              expectedDate: milestone.expectedDate || "",
              date: "",
              status: 0
            },
        ];

        return acc;
      },
      {}
    );

    setContainerWiseData(grouped);
    setIsSelected(true);
  };
  /* ================= DATE UPDATE ================= */

  const updateDate = (
    containerNumber: string,
    index: number,
    value: string
  ) => {
    setContainerWiseData((prev) => {
      const updated = { ...prev };
      updated[containerNumber][index] = {
        ...updated[containerNumber][index],
        date: value,
      };
      return updated;
    });
  };

  const removeDate = (containerNumber: string, index: number) => {
    updateDate(containerNumber, index, "");
  };

  /* ================= SAVE ================= */

  const saveMilestones = async () => {
    try {
      const payload: Milestone[] = [];

      Object.entries(containerWiseData).forEach(
        ([containerNumber, events]) => {
          events.forEach((event) => {
            payload.push({
              ...event,
              container_number: containerNumber,
              date: event.date || "",
            });
          });
        }
      );

      const payloadWithDate = payload.filter((element) => element.date != "");
      if (!payloadWithDate.length) {
        throw new Error("At least one milestone date is required");
      }

      await axiosInstance.post(
        "/api/v1/offline-ocean-milestone/create",
        payloadWithDate
      );

      toast.success("Milestones updated");
      await fetchMilestones();
    } catch (error: any) {
       let message = "Failed to update.";

    // ✅ Handle custom thrown error
    if (error.message) {
      message = error.message;
    }

    // ✅ Handle API error
    if (error.response?.data?.error) {
      message = error.response.data.error;
    }

    // ✅ Handle axios default message
    if (error.response?.data?.message) {
      message = error.response.data.message;
    }

    toast.error(message);
    }
  };

  return {
    uniqueMilestones,
    containerWiseData,
    selectMilestone,
    updateDate,
    removeDate,
    saveMilestones,
    loading,
    isSelected,
  };
};
